import { apiGet, apiPost, apiUpload } from './http';
// Taxonomia = SSOT partilhada (packages/shared) — antes duplicava rótulos/tipos.
import i18next from 'i18next';
import { CONTENT_KINDS as SHARED_KINDS, MARKET_CATEGORY_ORDER, kindLabel as sharedKindLabel } from '@marcianus/shared/taxonomy';

// ── Estúdio (CMS) — migrado da app lean (o www é produto; publicar é trabalho
// da equipa). MESMA API: POST /cms/content, POST /cms/products, GET /admin/content,
// POST /admin/content/generate (Hexis) e /studio/* (Hostinger headless).

export const CONTENT_KINDS = SHARED_KINDS;
export type ContentKind = (typeof CONTENT_KINDS)[number];
// Rótulo no idioma ativo (o slug é o identificador da BD).
export const kindLabel = (k?: string | null) =>
  i18next.t(`taxo.content.${(k || 'article').toLowerCase()}` as never, { ns: 'common', defaultValue: sharedKindLabel(k) }) as string;
export const MARKET_CATEGORIES = MARKET_CATEGORY_ORDER;

export const PUB_STATUS: Record<string, string> = {
  approved: 'Publicado', published: 'Publicado', draft: 'Rascunho', pending: 'Em revisão', review: 'Em revisão',
};
export const pubLabel = (s?: string | null) => {
  const key = !s ? 'draft' : (s === 'approved' || s === 'published') ? 'published' : (s === 'pending' || s === 'review') ? 'review' : s === 'draft' ? 'draft' : null;
  return key ? i18next.t(`studio.pub.${key}` as never, { ns: 'hq' }) as string : (s as string);
};

export interface ContentItem {
  id?: string | null;
  title?: string | null;
  content_type?: string | null;
  kind?: string | null;
  status?: string | null;
  publish_status?: string | null;
  sector?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  published_at?: string | null;
  author_id?: string | null;
  author_name?: string | null;
}

function toArray(raw: unknown): ContentItem[] {
  if (Array.isArray(raw)) return raw as ContentItem[];
  const d = (raw as { data?: unknown })?.data;
  return Array.isArray(d) ? (d as ContentItem[]) : [];
}

/** Lista de gestão (inclui rascunhos/pendentes). */
export async function listContent(): Promise<ContentItem[]> {
  return toArray(await apiGet<unknown>('/admin/content'));
}

export interface ContentDraft {
  title: string; kind: ContentKind; summary?: string; body_md?: string; sector?: string;
  cover_url?: string; media_url?: string;
}
export async function createContent(draft: ContentDraft) {
  // Caminho de STAFF (consola HQ), guardado por platform.cms.manage — NÃO o
  // /cms/content (submissão de TENANT, que exige role partner/organizer → 403
  // para o admin de plataforma). Cria como rascunho; a tabela publica depois.
  return apiPost<{ ok?: boolean; id?: string }>('/cms/admin/content', { is_free: true, ...draft });
}

export interface ProductDraft {
  name: string; category: string; price_kz: number;
  short_desc?: string; description?: string; image_url?: string; is_free?: boolean;
}
export async function createProduct(draft: ProductDraft) {
  return apiPost<{ ok?: boolean; id?: string }>('/cms/products', {
    name: draft.name, category: draft.category,
    price_kz: draft.is_free ? 0 : draft.price_kz,
    short_desc: draft.short_desc, description: draft.description,
    image_url: draft.image_url, currency: 'AOA',
  });
}

/** Copywriting com Hexis AI (DeepSeek API se configurada; senão Ollama local).
 *  mode: 'content' (editorial) | 'product' (ficha de marketplace: nome+desc). */
export async function generateWithHexis(params: { kind: string; keywords?: string[]; intent?: string; audience?: string; tone?: string; length?: string; mode?: 'content' | 'product'; locale?: string }) {
  const raw = await apiPost<{
    ok?: boolean;
    // Resposta canónica: { generated: {...}, meta: {...} }. Toleramos também o
    // formato antigo (campos no topo) para não partir se o backend variar.
    generated?: { title?: string; summary?: string; body?: string; body_md?: string; body_html?: string; tags?: string[]; cta?: string };
    meta?: { provider?: string; model?: string; target_words?: number };
    title?: string; summary?: string; body_md?: string; body?: string; body_html?: string; content?: string;
  }>(
    '/admin/content/generate',
    {
      kind: params.kind, keywords: params.keywords ?? [], intent: params.intent ?? '',
      ...(params.locale ? { locale: params.locale } : {}),
      audience: params.audience ?? '', tone: params.tone ?? 'institucional',
      length: params.length ?? 'médio', language: 'pt-AO', mode: params.mode ?? 'content',
    },
  );
  const g = (raw?.generated ?? raw ?? {}) as {
    title?: string; summary?: string; body?: string; body_md?: string; body_html?: string; content?: string; tags?: string[]; cta?: string;
  };
  return {
    title: g.title ?? '',
    summary: g.summary ?? '',
    // Corpo em HTML de blocos WordPress (pronto a publicar + renderiza igual no lean).
    body_md: g.body_html ?? g.body ?? g.body_md ?? g.content ?? '',
    tags: g.tags ?? [],
    cta: g.cta ?? '',
    provider: raw?.meta?.provider ?? '',
    model: raw?.meta?.model ?? '',
  };
}

// ── Imagens: upload manual + geração por IA (guardadas no nosso domínio) ──────
export async function uploadImage(file: File): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  const r = await apiUpload<{ ok?: boolean; url?: string; error?: string }>('/cms/admin/upload', form);
  if (!r?.url) throw new Error(r?.error || 'Upload falhou');
  return r.url;
}

export async function generateImage(input: {
  title?: string; summary?: string; body?: string; kind?: string; sector?: string;
  keywords?: string[]; width?: number; height?: number; prompt?: string;
}): Promise<{ url: string; prompt: string; size: string }> {
  const r = await apiPost<{ ok?: boolean; url?: string; prompt?: string; size?: string; error?: string }>(
    '/cms/admin/generate-image', input,
  );
  if (!r?.url) throw new Error(r?.error || 'Geração de imagem falhou');
  return { url: r.url, prompt: r.prompt || '', size: r.size || '' };
}

// ── Hostinger headless: conteúdos→WordPress, produtos→WooCommerce. O checkout
// mantém-se na Marcianus (Pay4all + carteira MXC).
export type StudioStatus = {
  wordpress: { enabled: boolean; writable: boolean; connected: boolean };
  woocommerce: { enabled: boolean; connected: boolean };
};
export async function getStudioStatus(): Promise<StudioStatus> {
  return apiGet<StudioStatus>('/studio/status').catch(() => ({
    wordpress: { enabled: false, writable: false, connected: false },
    woocommerce: { enabled: false, connected: false },
  }));
}

export type WpResult = { id: number; slug?: string; link: string; edit_link: string; status: string };
export async function publishToWordPress(input: { title: string; content?: string; excerpt?: string; status?: 'draft' | 'publish' }) {
  return apiPost<WpResult>('/studio/wordpress/posts', input);
}

export type WooResult = { id: string; slug?: string; permalink: string; status: string };
export async function publishToWooCommerce(input: { name: string; description?: string; short_description?: string; regular_price?: number | string; images?: string[]; status?: 'publish' | 'draft' }) {
  return apiPost<WooResult>('/studio/woocommerce/products', input);
}
